import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { RotateCcw, Save } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "../components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

const MERCHANT_OPTIONS = [
  { key: "blackfit", label: "Blackfit" },
  { key: "blackfit_premiere", label: "Blackfit Premiere" },
];

const MONTH_NAMES_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

const formatCurrency = (value) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(Number(value || 0));

const formatMonthLabel = (ym) => {
  if (!ym || ym.length < 7) return ym;
  const m = parseInt(ym.slice(5, 7), 10);
  return `${MONTH_NAMES_ID[m - 1]} ${ym.slice(0, 4)}`;
};

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

export default function MasterAccrue() {
  const [merchant, setMerchant] = useState("blackfit");
  const [month, setMonth] = useState(currentMonth);
  const [data, setData] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchOverrides = async (merchantKey = merchant, monthKey = month) => {
    try {
      setLoading(true);
      const { data: result } = await axios.get(`${API}/accounting/accrue/overrides`, {
        params: { merchant: merchantKey, month: monthKey },
        withCredentials: true,
      });
      setData(result);
      const next = {};
      for (const item of result.items || []) {
        next[item.key] = String(item.effective_amount ?? "");
      }
      setFormValues(next);
    } catch (error) {
      console.error("Failed to fetch accrue overrides:", error);
      toast.error(error.response?.data?.detail || "Gagal mengambil data accrue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverrides(merchant, month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant, month]);

  const items = useMemo(() => data?.items || [], [data]);
  const totalEffective = useMemo(
    () => items.reduce((sum, item) => sum + Number(formValues[item.key] || 0), 0),
    [items, formValues]
  );

  const isOverriddenInView = (item) =>
    Math.abs(Number(formValues[item.key] || 0) - Number(item.default_amount || 0)) > 0.005;

  const handleResetRow = (item) => {
    setFormValues((prev) => ({ ...prev, [item.key]: String(item.default_amount ?? 0) }));
  };

  const handleSave = async () => {
    const values = {};
    for (const item of items) {
      const raw = formValues[item.key];
      const parsed = Number(raw);
      if (raw === "" || raw === undefined || Number.isNaN(parsed) || parsed < 0) {
        toast.error(`Nilai ${item.label} tidak valid.`);
        return;
      }
      values[item.key] = parsed;
    }
    try {
      setSaving(true);
      await axios.put(`${API}/accounting/accrue/overrides`, { merchant, month, values }, { withCredentials: true });
      toast.success("Accrue tersimpan", {
        description: `Nilai accrue ${formatMonthLabel(month)} diperbarui untuk ${data?.merchant_label || merchant}.`,
      });
      fetchOverrides(merchant, month);
    } catch (error) {
      console.error("Failed to save accrue overrides:", error);
      toast.error(error.response?.data?.detail || "Gagal menyimpan accrue.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <Header title="Master Accrue" />
      <div className="space-y-6 p-8" data-testid="master-accrue-page">
        <section className="rounded-sm border border-white/10 bg-zinc-900/50 p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">Master Accrue</h2>
              <p className="mt-1 max-w-2xl text-sm text-zinc-500">
                Ubah nilai accrue per bulan untuk Blackfit dan Blackfit Premiere. Nilai yang disimpan dipakai di Laporan
                Bulanan (Rincian Profit) dan saldo Accounting untuk bulan terpilih.
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <span className="text-xs text-zinc-400">Merchant</span>
                <div className="flex gap-2">
                  {MERCHANT_OPTIONS.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setMerchant(option.key)}
                      className={`rounded-sm px-4 py-2 text-sm font-medium transition-colors ${
                        merchant === option.key
                          ? "bg-[#D4AF37] text-black"
                          : "bg-white/[0.03] text-zinc-300 ring-1 ring-white/[0.06] hover:bg-white/[0.06]"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <span className="text-xs text-zinc-400">Bulan</span>
                <Input
                  type="month"
                  value={month}
                  onChange={(event) => setMonth(event.target.value)}
                  className="w-44 rounded-sm border-transparent bg-white/[0.04] text-white ring-1 ring-white/[0.035] focus-visible:ring-white/15"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-sm border border-white/10 bg-zinc-900/50">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead>Item</TableHead>
                <TableHead className="text-right">Default</TableHead>
                <TableHead className="text-right">Nilai ({formatMonthLabel(month)})</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-zinc-500">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : items.length > 0 ? (
                <>
                  {items.map((item) => (
                    <TableRow key={item.key} className="border-white/5 hover:bg-zinc-900/50">
                      <TableCell className="py-4 text-sm font-semibold text-white">{item.label}</TableCell>
                      <TableCell className="py-4 text-right text-sm tabular-nums text-zinc-400">
                        {formatCurrency(item.default_amount)}
                      </TableCell>
                      <TableCell className="py-4 text-right">
                        <Input
                          type="number"
                          min="0"
                          step="any"
                          value={formValues[item.key] ?? ""}
                          onChange={(event) => setFormValues((prev) => ({ ...prev, [item.key]: event.target.value }))}
                          className="ml-auto w-48 rounded-sm border-transparent bg-white/[0.04] text-right text-white tabular-nums ring-1 ring-white/[0.035] focus-visible:ring-white/15"
                        />
                      </TableCell>
                      <TableCell className="py-4">
                        {isOverriddenInView(item) ? (
                          <span className="inline-flex rounded-sm bg-amber-500/10 px-2 py-1 text-xs font-medium text-amber-300">
                            Override
                          </span>
                        ) : (
                          <span className="inline-flex rounded-sm bg-zinc-800 px-2 py-1 text-xs font-medium text-zinc-400">
                            Default
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="py-4 text-right">
                        <button
                          type="button"
                          onClick={() => handleResetRow(item)}
                          title="Kembalikan ke default"
                          className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-white"
                        >
                          <RotateCcw className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableCell className="py-4 text-sm font-bold text-white">TOTAL NILAI ACCRUE</TableCell>
                    <TableCell />
                    <TableCell className="py-4 text-right text-sm font-bold tabular-nums text-[#D4AF37]">
                      {formatCurrency(totalEffective)}
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-zinc-500">
                    Tidak ada kategori accrue untuk merchant ini.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </section>

        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            className="rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]"
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Menyimpan..." : "Simpan"}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
